/**
 * @file Login.tsx
 * @description Sign-in with 2FA challenge (TOTP) support.
 */

import { useState } from "react";
import type { ChangeEvent } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { BrainCircuit, LineChart, UsersRound } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/axios";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // 🔐 2FA state
  const [twofaTicket, setTwofaTicket] = useState<string | null>(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setError("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setError("Please enter both email and password.");
      return;
    }

    try {
      if (busy) return;
      setBusy(true);
      await api.post("/auth/login", { email: normalizedEmail, password });
      // session is set via cookie
      navigate("/app");
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      // Not verified → nudge to resend page
      if (status === 403 && detail?.code === "EMAIL_NOT_VERIFIED") {
        navigate("/verify-reminder", { state: { email: detail.email ?? normalizedEmail } });
        return;
      }

      // Onboarding still required
      if (status === 403 && detail?.code === "ONBOARDING_REQUIRED") {
        navigate("/onboarding");
        return;
      }

      // ✅ 2FA required: show code prompt
      if (status === 403 && detail?.code === "TOTP_REQUIRED" && detail?.ticket) {
        setTwofaTicket(detail.ticket);
        setTwofaCode("");
        return;
      }

      if (status === 423) {
        setError("Account temporarily locked. Please try again in a few minutes.");
        return;
      }

      setError(
        status === 401
          ? "Invalid email or password."
          : (typeof detail === "string" && detail) || "Login failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleVerify2FA = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (!twofaTicket) return;
    const code = twofaCode.replace(/\D/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    try {
      if (busy) return;
      setBusy(true);
      await api.post("/auth/login/2fa", { ticket: twofaTicket, code });
      navigate("/app");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-white"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="w-full max-w-4xl bg-white shadow-xl rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* 🧠 Left panel */}
        <div className="bg-black text-white p-10 flex flex-col justify-center">
          <h1 className="text-2xl font-bold mb-4">Welcome to Infuse Ed</h1>
          <p className="mb-6">
            Run online student support, engagement, and retention — in one place, and in half the time.
          </p>
          <ul className="space-y-4">
            <motion.li className="flex items-center gap-3" whileHover={{ scale: 1.05 }}>
              <BrainCircuit className="text-white w-6 h-6" />
              <span>Modular platform for AI-driven education</span>
            </motion.li>
            <motion.li className="flex items-center gap-3" whileHover={{ scale: 1.05 }}>
              <LineChart className="text-white w-6 h-6" />
              <span>Real-time analytics & automation</span>
            </motion.li>
            <motion.li className="flex items-center gap-3" whileHover={{ scale: 1.05 }}>
              <UsersRound className="text-white w-6 h-6" />
              <span>Human-centered learning tools</span>
            </motion.li>
          </ul>
        </div>

        {/* 🔐 Right panel */}
        <div className="p-10">
          <h2 className="text-xl font-bold mb-4">Sign In</h2>

          {error && (
            <motion.div
              className="mb-4 text-red-600"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          {/* Normal creds form */}
          {!twofaTicket ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                autoComplete="email"
                required
                disabled={busy}
                className={error && !email ? "border-red-500 shadow-sm" : ""}
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
                required
                disabled={busy}
                className={error && !password ? "border-red-500 shadow-sm" : ""}
              />
              <p className="text-sm text-right">
                <Link to="/forgot-password" className="underline hover:text-black">
                  Forgot password?
                </Link>
              </p>
              <motion.div whileHover={{ scale: 1.02 }}>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
                >
                  {busy ? "Signing in…" : "Sign In"}
                </Button>
              </motion.div>
              <p className="text-sm text-center">
                Don’t have an account?{" "}
                <Link to="/signup" className="underline hover:text-black font-medium">
                  Sign Up
                </Link>
              </p>
            </form>
          ) : (
            // 2FA prompt
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <p className="text-gray-700">Enter the 6-digit code from your authenticator app.</p>
              <Input
                placeholder="123456"
                inputMode="numeric"
                pattern="\d*"
                value={twofaCode}
                onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center tracking-widest"
                autoComplete="one-time-code"
                required
                disabled={busy}
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={busy}
                  className="flex-1 bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black"
                >
                  {busy ? "Verifying…" : "Verify & Continue"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setTwofaTicket(null);
                    setTwofaCode("");
                    setError("");
                  }}
                >
                  Back
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
}
